import Battery0BarTwoToneIcon from '@mui/icons-material/Battery0BarTwoTone'
import Battery6BarTwoToneIcon from '@mui/icons-material/Battery6BarTwoTone'
import BatteryChargingFullTwoToneIcon from '@mui/icons-material/BatteryChargingFullTwoTone'
import BatterySaverTwoToneIcon from '@mui/icons-material/BatterySaverTwoTone'
import dataStore from '../../../store/dataStore.ts'
import { IPet } from '../../../interfaces/types.ts'
import { SubItem } from './SubItem.tsx'

interface Props {
    pet: IPet
    mini?: boolean
    mobile?: boolean
    dashboard?: boolean
    iconOnly?: boolean
}

type BatteryColor = 'disabled' | 'error' | 'warning' | 'success' | 'info'

const getIcon2 = (on: boolean, charging: boolean, beacon: boolean, percent: number, mobile: boolean) => {
    const style = mobile ? {
        height: '33.5px',
        width: '33.5px',
        border: '2.25px white solid'
    } : {
        height: '40px',
        width: '40px',
        border: '2px white solid'
    }

    const color: BatteryColor = percent === 0 ? 'disabled'
        : percent < 21 ? 'error'
            : percent < 51 ? 'warning'
                : 'success'

    const icon = charging ?
        <BatteryChargingFullTwoToneIcon fontSize={'large'} color={color}/>
        : !on ?
            <Battery0BarTwoToneIcon fontSize={'large'} color={'disabled'}/>
            : beacon ?
                <BatterySaverTwoToneIcon fontSize={'large'} color={color}/>
                : <Battery6BarTwoToneIcon fontSize={'large'} color={color}/>

    return (
        <div className={'rounded-full p-[1px] bg-white'} style={style}>
            {icon}
        </div>
    )
}

// Derive all display values from device state in one pass. Pure function, no hooks.
const deriveBatteryDisplay = (powerOn: boolean, charging: boolean, batteryAlert: number, batteryPercent: string) => {
    const isOff = !powerOn
    let status: 'off' | 'charging' | 'low' | 'on'
    let colorName: BatteryColor

    if (isOff) {
        status = 'off'
        colorName = 'disabled'
    } else if (charging) {
        status = 'charging'
        colorName = 'info'
    } else if (batteryAlert) {
        status = 'low'
        colorName = 'error'
    } else {
        status = 'on'
        const val = parseFloat(batteryPercent)
        colorName = val >= 50 ? 'success' : val >= 20 ? 'warning' : 'error'
    }

    const displayPercent = isOff ? 'off' : batteryPercent
    const colorClass = colorName === 'info' ? 'text-primary' : `text-${colorName}`

    return { isOff, status, colorClass, displayPercent }
}

export const Battery = (props: Props) => {
    const { pet, mobile, iconOnly } = props
    const devices = dataStore((s) => s.devices)
    const device = devices.find(d => d.petId === pet._id) ?? null

    if (!device) return null

    const { powerOn, charging, beaconInRange, batteryAlert, batteryPercent } = device.status
    const { isOff, status, colorClass, displayPercent } = deriveBatteryDisplay(
        powerOn,
        charging,
        batteryAlert,
        batteryPercent
    )

    const iconPercent = isOff ? 0 : parseFloat(batteryPercent) || 0
    const icon = getIcon2(powerOn, charging, beaconInRange, iconPercent, Boolean(mobile))

    const subItem = (
        <SubItem pet={pet} title={'Battery'} icon={icon} className={'-ml-1 -mr-2'} mobile={mobile}>
            <div className={'flex items-center justify-between'}>
                <div className={`${colorClass} font-md text-lg`}>
                    {!isOff
                        ? <span> Battery at <span className={'font-semibold'}>{displayPercent}</span></span>
                        : <span className={'font-semibold'}>Off</span>}
                </div>
                <div className={`text-md ${colorClass}`}>
                    Tracker is {status}
                </div>
            </div>
        </SubItem>
    )

    const batteryIcon = (
        <div className={'grid grid-rows-2 gap-1'}>
            <div className={'justify-self-center'}>{icon}</div>
            <div className={'bg-transparent text-primary-content justify-self-center'}>{displayPercent}</div>
        </div>
    )

    return iconOnly ? batteryIcon : subItem
}
